/**
 * DecisionBandSection (FIN-2) — the Financial Viability VERDICT band.
 *
 * READ-ONLY over `report.financialAnalysis` (the deterministic engine stamp)
 * plus the ONE display verdict (`resolveFinancialDisplayVerdict`). Renders the
 * banded verdict (Viable / Conditional / Unfinanceable / withheld), the
 * viability score, the binding constraints that held the band down, the
 * auditable rationale, and an engine-provenance chip — all THEME-AWARE via
 * roleColor() so the band follows the active report preset (warm paper, cool
 * white, monograph, etc.). No bespoke hardcoded colors; verdict tone is mapped
 * through roleColor positive / warning / negative.
 *
 * ONE VERDICT PER REPORT (decision Q12, review 2026-08-19). This section used
 * to render the RAW engine band (`fa.band`) while the cover, dashboard,
 * Go/No-Go rollup and the DOCX tint rendered `getDisplayVerdict`, which stacks
 * the evidence-tier (EA8), firm-capacity (ELCC) and binding-constraint caps on
 * top — so a "Viable · 82/100" body could sit under a "Conditional" cover with
 * nothing on the page explaining the difference. It now renders the SAME
 * displayed tier as every other surface, and when that tier differs from the
 * model's own band it prints the cap note and NAMES the cap. The reader never
 * sees two verdicts without an explanation of the gap.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import {
  resolveFinancialDisplayVerdict,
  type VerdictTier,
} from '../../../../lib/reportVerdict';
import { EditorialBadge } from '../../report/editorial';
import {
  FIN_MONO,
  bandTone,
  type FinTone,
} from '../../../../lib/reportRendering/financialFormat';

const TONE_ROLE: Record<FinTone, 'positive' | 'warning' | 'negative' | 'accent'> = {
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
  neutral: 'accent',
};

/**
 * Displayed TIER → editorial tone. The section tones off the DISPLAYED tier,
 * not the raw band, so the colour of the band matches the word inside it (and
 * matches the cover). `financialFormat.bandTone` keeps its band signature for
 * the sections that legitimately still describe the model itself.
 */
function tierTone(tier: VerdictTier): FinTone {
  if (tier === 'green') return 'positive';
  if (tier === 'amber') return 'warning';
  if (tier === 'red') return 'negative';
  return 'neutral';
}

export const DecisionBandSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const fa = report.financialAnalysis;
  const hasData = Boolean(fa);

  // The ONE verdict — identical to the cover / dashboard / rollup / DOCX tint.
  const fv = resolveFinancialDisplayVerdict(report);
  const tone = tierTone(fv.tier);
  const verdictRole = TONE_ROLE[tone];
  const verdictInk = roleColor(theme, verdictRole);
  const score = typeof fa?.viabilityScore === 'number' && Number.isFinite(fa.viabilityScore)
    ? Math.round(fa.viabilityScore)
    : null;

  // RP4 — surface the resolve prompt when the financial verdict is WITHHELD (no
  // engine stamp, or a stamp whose band the engine declined to set) or
  // UNFINANCEABLE (displayed tier 'red'). This is a context-free in-report
  // section (also rendered to PDF). A linked report routes to the owning site's
  // LIVE Financial Model tab, where the inputs that set this verdict are
  // edited (it used to open the retired Path to GO tab); a historical report
  // without `siteId` remains a read-only artifact and does not manufacture a
  // broken site URL. The whole prompt is guarded `[data-screen-only-cta]` +
  // `no-print` so it NEVER renders in the printed / exported deck.
  const showResolveCta = !hasData || fv.withheld || fv.tier === 'red';
  const liveFinancialModelHref = report.siteId
    ? `/sites/${encodeURIComponent(report.siteId)}?tab=financial`
    : null;

  return (
    <>
    <SectionShell label={label} hasData={hasData}>
      {fa ? (
        <div className="space-y-4">
          {/* William QA 2026-06-28 — left accent rule removed report-wide */}
          <div
            className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3"
            style={{
              background: roleColorAlpha(theme, verdictRole, 0.07),
            }}
            data-testid="financial-decision-band"
            data-verdict-tier={fv.tier}
            data-model-band={fv.modelBand ?? 'withheld'}
          >
            <span
              className="text-xl font-bold"
              style={{ color: verdictInk }}
              data-testid="financial-verdict-label"
            >
              {fv.label}
            </span>
            {score != null ? (
              <span
                className="text-sm"
                style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}
              >
                Viability score {score}/100
              </span>
            ) : null}
            <span className="grow" />
            <EditorialBadge theme={theme} tone="neutral" variant="outline" title="Deterministic engine provenance">
              projectFinance · finDecision
            </EditorialBadge>
          </div>

          {/* WITHHELD — the engine ran and declined to band. Say WHICH kind:
              "cannot compute" and "computed but incoherent" have different
              fixes, and telling a user to add data when the real problem is an
              incoherent CAPEX basis sends them the wrong way. */}
          {fv.withheld ? (
            <div
              className="px-4 py-3"
              style={{
                background: roleColorAlpha(theme, 'accent', 0.05),
                borderLeft: `2px solid ${roleColor(theme, 'accent')}`,
              }}
              data-testid="financial-verdict-withheld-note"
              data-withheld-reason={fv.withheldReason ?? 'unspecified'}
            >
              <div
                className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}
              >
                Why the verdict is withheld
              </div>
              <p className="text-sm" style={{ color: roleColor(theme, 'body') }}>
                {fv.verdict.withheldVerdict?.note ?? 'The deterministic engine did not band these inputs.'}
              </p>
            </div>
          ) : null}

          {/* CAP DISCLOSURE (Q12) — the displayed tier is more cautious than the
              model's own band. Print BOTH and name every cap that bound; a cap
              the reader cannot see is indistinguishable from a contradiction. */}
          {fv.capped ? (
            <div
              className="px-4 py-3"
              style={{
                background: roleColorAlpha(theme, 'warning', 0.05),
                borderLeft: `2px solid ${roleColor(theme, 'warning')}`,
              }}
              data-testid="financial-verdict-cap-note"
              data-cap-kinds={fv.caps.map(c => c.kind).join(',')}
            >
              <div
                className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}
              >
                Displayed verdict capped
              </div>
              <p className="text-sm" style={{ color: roleColor(theme, 'body') }}>
                The financial model bands this{' '}
                <strong style={{ color: roleColor(theme, TONE_ROLE[bandTone(fv.modelBand)]) }}>
                  {fv.modelLabel}
                </strong>
                ; the report displays <strong style={{ color: verdictInk }}>{fv.label}</strong>. The caps
                below only ever lower a verdict — they never raise one.
              </p>
              <ul className="space-y-1 mt-1.5">
                {fv.caps.map((c, i) => (
                  <li
                    key={`cap-${i}`}
                    className="text-sm flex gap-2"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <span aria-hidden style={{ color: roleColor(theme, 'warning') }}>•</span>
                    <span>
                      <strong>{c.name}</strong> — {c.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Binding constraints — what held the band down (empty when Viable). */}
          {fa.bindingConstraints.length > 0 ? (
            <div>
              <div
                className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}
              >
                Binding constraints
              </div>
              <ul className="space-y-1">
                {fa.bindingConstraints.map((c, i) => (
                  <li
                    key={`bind-${i}`}
                    className="text-sm flex gap-2"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <span aria-hidden style={{ color: roleColor(theme, 'negative') }}>•</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Auditable rationale — why the band landed where it did. */}
          {fa.rationale.length > 0 ? (
            <div>
              <div
                className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
                style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}
              >
                Rationale
              </div>
              <ul className="space-y-1">
                {fa.rationale.map((r, i) => (
                  <li
                    key={`rat-${i}`}
                    className="text-sm flex gap-2"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <span aria-hidden style={{ color: roleColor(theme, 'accent') }}>–</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Two-engine LCOE divergence note — surfaced, not hidden. */}
          {fa.lcoeEngineDiffNote ? (
            <p className="text-[11px]" style={{ color: roleColor(theme, 'muted') }}>
              {fa.lcoeEngineDiffNote}
            </p>
          ) : null}
        </div>
      ) : null}
    </SectionShell>

    {/* RP4 — screen-only resolve pointer. Linked reports route to the owning
         site's live Financial Model tab. Historical reports without a
         siteId remain readable but do not receive a false live navigation
         target. `data-screen-only-cta` hides this under @media print AND under
         the server-PDF export root. */}
    {showResolveCta && (
      <div
        data-screen-only-cta
        data-testid="financial-resolve-cta"
        className="no-print mt-2 flex items-center gap-2 text-[12px]"
      >
        <span style={{ color: roleColor(theme, 'muted') }}>
          {!hasData
            ? 'Financial verdict withheld – no engine stamp yet.'
            : fv.withheld
              ? 'Financial verdict withheld – the engine did not band these inputs.'
              : 'Unfinanceable on current inputs.'}
        </span>
        {liveFinancialModelHref ? (
          <a
            href={liveFinancialModelHref}
            className="font-bold text-primary hover:underline"
            style={{ color: roleColor(theme, 'accent') }}
          >
            Resolve in the live Financial Model →
          </a>
        ) : (
          <span
            data-testid="financial-resolve-live-site-unavailable"
            style={{ color: roleColor(theme, 'muted') }}
          >
            Open the owning site's Financial Model to resolve this.
          </span>
        )}
      </div>
    )}
    </>
  );
};
